// ===========================================
// The school's saved / eligible discounts, as a tick list (2026-10-02)
// ===========================================
// Extracted from InvoiceDiscountApprovalModal so the draft Edit dialog can offer the
// same list (MNM: "when editing an invoice, add a product or discount"; Andrew,
// D1 = both a typed one-off AND the saved discounts). The ids are the ones the
// server's discount preview gives: a catalogue discount's own id, or a synthetic
// `variable-…` id for the family discount.
//
// `showAmounts`: the modal shows each discount's dollar figure, worked out on the
// invoice's STORED lines. The Edit dialog does not — its lines may already have
// changed on screen, so a figure here would be stale; the live totals preview shows
// what each ticked discount comes to.

import { Box, Checkbox, Chip, FormControlLabel, FormGroup, Stack, Typography } from '@mui/material';
import { DiscountType, type CalculatedDiscount } from '../../types/discount.types';
import { coverageSuffix } from '../../utils/discountCoverage';

/** Plain words for the type chip — never a raw enum name on screen. */
const TYPE_LABEL: Record<DiscountType, string> = {
  [DiscountType.VARIABLE_FAMILY]: 'Family',
  [DiscountType.VARIABLE_INSTRUMENT]: 'Instrument',
  [DiscountType.VARIABLE_PROGRAM_LEVEL]: 'Program level',
  [DiscountType.CUSTOM]: 'Custom',
};

const SCOPE_LABEL: Record<string, string> = {
  FAMILY: 'Whole family',
  STUDENT: 'Student',
  INVOICE_ITEM: 'Line item',
};

interface EligibleDiscountChecklistProps {
  discounts: CalculatedDiscount[];
  selectedIds: string[];
  onToggle: (discountId: string, checked: boolean) => void;
  /** Show the dollar figure on the invoice's stored lines (the modal). */
  showAmounts?: boolean;
}

function discountTypeLabel(type: DiscountType | undefined): string | null {
  return type ? (TYPE_LABEL[type] ?? null) : null;
}

export default function EligibleDiscountChecklist({
  discounts,
  selectedIds,
  onToggle,
  showAmounts = false,
}: EligibleDiscountChecklistProps) {
  return (
    <FormGroup data-testid="eligible-discount-checklist">
      {discounts.map((discount) => {
        const checked = selectedIds.includes(discount.id);
        const typeLabel = discountTypeLabel(discount.type);
        const rate = discount.isPercent
          ? `${discount.percentage || discount.value}%`
          : `$${Number(discount.value).toFixed(2)}`;
        return (
          <Box
            key={discount.id}
            sx={{
              mb: 1,
              px: 1.5,
              py: 1,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: checked ? 'action.selected' : 'background.paper',
            }}
          >
            <FormControlLabel
              control={<Checkbox checked={checked} onChange={(e) => onToggle(discount.id, e.target.checked)} />}
              label={
                <Stack spacing={0.5} sx={{ flex: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="body1" fontWeight={500}>
                      {discount.name}
                    </Typography>
                    {typeLabel && <Chip label={typeLabel} size="small" variant="outlined" />}
                    {discount.scope && (
                      <Chip
                        label={SCOPE_LABEL[discount.scope] ?? discount.scope}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {rate}
                    {/* #625 D9: what it covers, as approve will store it. */}
                    {coverageSuffix(discount.coverageLabel)}
                    {showAmounts && (
                      <>
                        {' → '}
                        <strong>${discount.calculatedAmount.toFixed(2)}</strong> savings
                      </>
                    )}
                  </Typography>
                </Stack>
              }
            />
          </Box>
        );
      })}
    </FormGroup>
  );
}
